// TypeAutomationService.js
//
// System-type automation templates live under `_nodetypes/{typeKey}/automations/{ruleKey}`.
// They are live for every base that uses the type (no per-base provision).

import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';
import { generateKey } from 'utilities/Keys';

const NODETYPES_COLLECTION = collections.nodetypes;
const TYPE_AUTOMATIONS_SUBCOLLECTION = collections.nodeTypeAutomations || 'automations';

function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * @param {object|null|undefined} rule
 * @returns {boolean}
 */
export function isSystemTypeAutomation(rule) {
  if (!rule || typeof rule !== 'object') return false;
  if (rule.isSystemAutomation === true) return true;
  if (normalizeString(rule.automationSource).toUpperCase() === 'SYSTEM_TYPE') return true;
  // Top-level Firestore classifier is a string; nested UI scope is an object.
  if (typeof rule.scope === 'string'
    && normalizeString(rule.scope).toUpperCase() === 'SYSTEM_TYPE') {
    return true;
  }
  return false;
}

/**
 * @param {string} typeKey
 * @param {object} data
 * @param {string} [docId]
 * @returns {object}
 */
export function normalizeSystemTypeAutomationRow(typeKey, data = {}, docId = '') {
  const cleanedTypeKey = normalizeString(typeKey);
  const key = normalizeString(data?.key) || normalizeString(docId);
  const nestedScope = (data?.scope && typeof data.scope === 'object')
    ? data.scope
    : null;

  return {
    ...(data || {}),
    id: key || normalizeString(docId),
    key: key || normalizeString(docId),
    nodeType: cleanedTypeKey || normalizeString(data?.nodeType),
    // Document classifier (string). Nested UI scope lives under ruleScope if present.
    scope: 'SYSTEM_TYPE',
    ruleScope: nestedScope || {
      databaseKey: '',
      nodeTypeKey: cleanedTypeKey,
    },
    isSystemAutomation: true,
    automationSource: 'SYSTEM_TYPE',
    baseKey: null,
    canDelete: false,
  };
}

function typeAutomationsCollectionRef(typeKey) {
  return collection(
    db,
    NODETYPES_COLLECTION,
    normalizeString(typeKey),
    TYPE_AUTOMATIONS_SUBCOLLECTION,
  );
}

function typeAutomationDocRef(typeKey, ruleKey) {
  return doc(
    db,
    NODETYPES_COLLECTION,
    normalizeString(typeKey),
    TYPE_AUTOMATIONS_SUBCOLLECTION,
    normalizeString(ruleKey),
  );
}

/**
 * Subscribe to system automations for a root node type.
 *
 * @param {string} typeKey
 * @param {function(object[]): void} callback
 * @returns {function(): void}
 */
export function subscribeToTypeAutomations(typeKey, callback) {
  const cleanedTypeKey = normalizeString(typeKey);
  if (!cleanedTypeKey || typeof callback !== 'function') {
    if (typeof callback === 'function') callback([]);
    return () => {};
  }

  return onSnapshot(
    typeAutomationsCollectionRef(cleanedTypeKey),
    (snapshot) => {
      const rows = snapshot.docs.map((docSnap) => (
        normalizeSystemTypeAutomationRow(cleanedTypeKey, docSnap.data() || {}, docSnap.id)
      ));
      callback(rows);
    },
    (error) => {
      console.error('[TypeAutomationService] subscribeToTypeAutomations error:', error);
      callback([]);
    },
  );
}

/**
 * Create or replace a system-type automation template.
 *
 * @param {string} typeKey
 * @param {object} rule
 * @returns {Promise<object>}
 */
export async function saveTypeAutomation(typeKey, rule = {}) {
  const cleanedTypeKey = normalizeString(typeKey);
  if (!cleanedTypeKey) {
    throw new Error('typeKey is required to save a system automation.');
  }

  const ruleKey = normalizeString(rule?.key) || generateKey();
  const now = Timestamp.now();
  const existingCreated = rule?.dateCreated || null;
  const title = normalizeString(rule?.title) || 'Untitled Automation';

  const nestedScope = (rule?.ruleScope && typeof rule.ruleScope === 'object')
    ? rule.ruleScope
    : (rule?.scope && typeof rule.scope === 'object' ? rule.scope : null);

  const payload = {
    ...rule,
    key: ruleKey,
    title,
    nodeType: cleanedTypeKey,
    scope: 'SYSTEM_TYPE',
    isSystemAutomation: true,
    automationSource: 'SYSTEM_TYPE',
    baseKey: null,
    userKey: null,
    enabled: rule?.enabled !== false,
    canDelete: false,
    dateCreated: existingCreated || now,
    dateModified: now,
  };

  // Prefer nested UI scope under a dedicated key so it does not collide with
  // the top-level SYSTEM_TYPE classifier string.
  if (nestedScope) {
    payload.ruleScope = {
      databaseKey: normalizeString(nestedScope.databaseKey || nestedScope.collectionKey),
      nodeTypeKey: cleanedTypeKey,
    };
  } else {
    payload.ruleScope = {
      databaseKey: '',
      nodeTypeKey: cleanedTypeKey,
    };
  }

  delete payload.id;

  await setDoc(typeAutomationDocRef(cleanedTypeKey, ruleKey), payload, { merge: false });
  return normalizeSystemTypeAutomationRow(cleanedTypeKey, payload, ruleKey);
}

/**
 * Delete a system-type automation template.
 *
 * @param {string} typeKey
 * @param {string} ruleKey
 * @returns {Promise<void>}
 */
export async function deleteTypeAutomation(typeKey, ruleKey) {
  const cleanedTypeKey = normalizeString(typeKey);
  const cleanedRuleKey = normalizeString(ruleKey);
  if (!cleanedTypeKey || !cleanedRuleKey) return;
  await deleteDoc(typeAutomationDocRef(cleanedTypeKey, cleanedRuleKey));
}

/**
 * Merge system automations ahead of base automations; base rows with the same key are dropped.
 *
 * @param {object[]} systemRules
 * @param {object[]} baseRules
 * @returns {object[]}
 */
export function mergeSystemAndBaseAutomations(systemRules = [], baseRules = []) {
  const systemRows = (Array.isArray(systemRules) ? systemRules : [])
    .map((row) => normalizeSystemTypeAutomationRow(row?.nodeType, row, row?.key || row?.id));
  const systemKeys = new Set(
    systemRows.map((row) => normalizeString(row?.key || row?.id)).filter(Boolean),
  );

  const baseRows = (Array.isArray(baseRules) ? baseRules : []).filter((row) => {
    const key = normalizeString(row?.key || row?.id);
    return key && !systemKeys.has(key);
  });

  return [...systemRows, ...baseRows];
}
